import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface ConnectPermission {
  chat: boolean;
  meeting: boolean;
}

export interface AttendancePermission {
  admin: boolean;
  employee: boolean;
}

export interface EmployeesPermission {
  department: boolean;
  leaveEmployees: boolean;
  adminLeave: boolean;
  leaveConfiguration: boolean;
}

export interface ReportsPermission {
  leaveReports: boolean;
  attendanceReports: boolean;
}

export interface Permissions {
  dashboard: boolean;
  connect: ConnectPermission;
  projects: boolean;
  allProjects: boolean;
  notifications: boolean;
  ourClients: boolean;
  userProfile: boolean;
  holidays: boolean;
  attendance: AttendancePermission;
  employees: EmployeesPermission;
  reports: ReportsPermission;
  adminSettings: boolean;
  superAdminPanel: boolean;
}

export interface RolePermission {
  _id: string;
  role: string;
  permissions: Permissions;
  organizationId: string;
  parentRole?: string | null;
  isDefault?: boolean;
  userCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export type RolePermissionPayload = {
  role: string;
  permissions: Permissions;
  organizationId: string;
  parentRole?: string | null;
};

export type RolePermissionResponse = {
  success: boolean;
  message: string;
  data: RolePermission;
};

const addRolePermission = async (
  payload: RolePermissionPayload
): Promise<RolePermissionResponse> => {
  const { data } = await customAxios.post<RolePermissionResponse>(
    "/permissions/roles",
    payload
  );
  return data;
};

export const useAddRolePermission = () => {
  const queryClient = useQueryClient();

  return useMutation<RolePermissionResponse, AxiosError, RolePermissionPayload>(
    {
      mutationFn: addRolePermission,
      onSuccess: async (data) => {
        showNotification({
          title: "Success",
          message: data.message,
          color: "green",
        });

        await queryClient.invalidateQueries(["permissions-roles"]);
      },
      onError: (error) =>
        showNotification({
          title: "Error",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to add role permission",
          color: "red",
        }),
    }
  );
};
